import { config } from "@/config";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { ChatResponse } from "@/types/chat";
import { useInfiniteQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

const CHAT_PAGE_LIMIT = 10;

export async function getChatByuuid(
  uuid: string,
  agent: string,
  token: string,
  page: number = 1,
  limit: number = CHAT_PAGE_LIMIT,
  threadId?: string
): Promise<ChatResponse> {
  const params = new URLSearchParams({
    agent,
    page: String(page),
    limit: String(limit),
  });
  if (threadId) params.set("threadId", threadId);
  const url = `${config.isometricApiUrl}/chat/byUUID/${uuid}?${params.toString()}`;

  const response = await apiFetch(url, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  // Check if the response is ok (status code in the range 200-299)
  if (!response.ok) {
    const error: any = new Error(`HTTP error! status: ${response.status}`);
    error.status = response.status;
    throw error;
  }

  return await response.json();
}
export default function useGetChatService(
  uuid: string,
  agent: string,
  enabled?: boolean,
  threadId?: string
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useInfiniteQuery({
    queryKey: ["chat-by-id", uuid, agent, threadId ?? null],
    queryFn: ({ pageParam = 1 }) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return getChatByuuid(uuid || "", agent || "", token, pageParam, CHAT_PAGE_LIMIT, threadId);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce((sum, p) => sum + p.data.length, 0);
      if (totalLoaded < lastPage.total) {
        return allPages.length + 1;
      }
      return undefined;
    },
    // A missing thread will not appear on retry; fail fast so the UI can tell the user.
    retry: (failureCount, error) =>
      (error as { status?: number }).status !== 404 && failureCount < 3,
    enabled: !!enabled,
  });
}
